// Single source of truth for the workspace API base URL.
//
// Wraps `@symbo.ls/channels` — the canonical platform-wide channel
// registry — so every consumer that hits `/workspace/*` (SDK init in
// sdk.js, the raw Supabase passthrough in supabaseConfig.js, the
// workspace-project SDK adapter in envs.js) resolves to the same URL
// derived from a single channel name. Channels (`local`, `localNext`,
// `next`, `staging`, `upcoming`, `production`, ...) are defined in
// `server/packages/channels/channels.json`.
//
// Hostname → channel autodetection ties each deployed env to its
// matching API channel without per-bundle config:
//
//   my.localhost                  → localNext  (local express bridge)
//   *.localhost                   → localNext
//   dev.my.symbols.app            → development
//   next.my.symbols.app           → next
//   upcoming.my.symbols.app       → upcoming
//   test.my.symbols.app           → test
//   staging.my.symbols.app        → staging
//   my.symbols.app                → production
//
// The same prefix → channel rule applies to canvas.symbols.app,
// workspace.symbols.app, and any other shell host using the same DNS
// pattern, so env subdomains stay in lockstep across the platform.
//
// Precedence (first non-null wins):
//   1. `window.__SYMBOLS_ENV.WORKSPACE_API_URL`  — explicit URL override,
//      escape hatch for ngrok / mirrors / staging proxies.
//   2. `window.__SYMBOLS_ENV.SYMBOLS_API_CHANNEL` — channel name override.
//   3. `localStorage.symbols_workspace_api`     — explicit URL, persists.
//   4. `localStorage.symbols_api_channel`       — channel name, persists.
//   5. Hostname autodetect (table above).
//   6. `apiUrl()` from `@symbo.ls/channels`     — registry's own
//      resolution (NODE_ENV → SYMBOLS_API_CHANNEL → defaultChannel).
//
// Runtime override via URL — for testing how a server PR-preview deploy
// affects the live UI without rebuilding the frontend:
//   ?apiUrl=https://pr-xyz.api.symbols.app  → store + use
//   ?apiUrl=clear                           → drop the override
// On first call we capture the param into `localStorage.symbols_workspace_api`
// so subsequent navigations keep using it without re-passing the param.
// PROD HOSTS (my.symbols.app / edit.symbols.app / preview.symbols.app)
// hard-ignore this param so a malicious link can't repoint a logged-in
// user's shell at a foreign API origin.

import { apiUrl as channelApiUrl, isKnownChannel } from '@symbo.ls/channels'

// Maps a hostname's leading subdomain to its API channel. Returns null
// when no env prefix matches → caller falls through to the channel
// registry's default (= `production`).
//
// Recognizes:
//   1. `<env>.my.symbols.app`         (apex shell host: dev/next/upcoming/test/staging)
//   2. `<env>.<other>.symbols.app`    (canvas, workspace, … — same prefix rule)
//   3. `*.localhost` / `localhost`    → `localNext`
const _hostToChannel = (host) => {
  if (!host) return null
  if (host === 'localhost' || host.endsWith('.localhost')) return 'localNext'
  const labels = host.split('.')
  if (labels.length < 4) return null   // bare apex `my.symbols.app`
  const prefix = labels[0]
  // `dev` channel is named `development` in the registry — alias it.
  if (prefix === 'dev') return 'development'
  if (prefix && isKnownChannel(prefix)) return prefix
  return null
}

// Production hosts that MUST NEVER honor a runtime ?apiUrl override —
// safety guard so a malicious link can't repoint a logged-in user's
// shell at a different API origin and exfiltrate tokens.
const _PROD_HOSTS = new Set([
  'my.symbols.app',
  'edit.symbols.app',
  'preview.symbols.app'
])
const _isProdHost = (host) => _PROD_HOSTS.has(host || '')

// Apply a `?apiUrl=...` query-param override into localStorage so the
// rest of the precedence chain (and every subsequent page load) honors
// it. Designed for testing how a server PR-preview deploy
// (e.g. https://pr-xyz.api.symbols.app) affects the live UI without
// rebuilding the frontend.
//
// Behavior:
//   ?apiUrl=https://pr-xyz.api.symbols.app  → store + use
//   ?apiUrl=clear  (or  ?apiUrl=  blank)    → remove the override
// Non-prod hosts only — prod is hard-locked.
//
// Idempotent: only applies once per page load (URL param is the trigger,
// after which the localStorage key carries the override across navs).
let _queryApplied = false
const _applyQueryApiOverride = () => {
  if (_queryApplied) return
  _queryApplied = true
  if (typeof window === 'undefined') return
  if (_isProdHost(window.location.hostname)) return
  let raw
  try {
    raw = new URLSearchParams(window.location.search).get('apiUrl')
  } catch (_) { return }
  if (raw == null) return
  const trimmed = raw.trim()
  try {
    if (trimmed === '' || trimmed.toLowerCase() === 'clear') {
      window.localStorage.removeItem('symbols_workspace_api')
      return
    }
    // Reject anything that doesn't look like an http(s) URL — guard
    // against `?apiUrl=javascript:...` or relative path injection.
    const u = new URL(trimmed)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return
    window.localStorage.setItem('symbols_workspace_api', u.origin)
  } catch (_) { /* invalid URL — ignore */ }
}

export function workspaceApiBase () {
  if (typeof window === 'undefined') return channelApiUrl()
  // First-call only — capture the ?apiUrl=... query param into LS so
  // every subsequent resolution (this call + any navigation) reads it
  // through the lsUrl branch below.
  _applyQueryApiOverride()
  // Explicit URL override (highest precedence — direct URL beats channel).
  const runtimeUrl = window.__SYMBOLS_ENV?.WORKSPACE_API_URL
  if (runtimeUrl) return runtimeUrl
  // Channel name override at runtime.
  const runtimeChannel = window.__SYMBOLS_ENV?.SYMBOLS_API_CHANNEL
  if (runtimeChannel) return channelApiUrl(runtimeChannel)
  // localStorage — explicit URL flag survives reloads.
  try {
    const lsUrl = window.localStorage.getItem('symbols_workspace_api')
    if (lsUrl) return lsUrl
    const lsChannel = window.localStorage.getItem('symbols_api_channel')
    if (lsChannel) return channelApiUrl(lsChannel)
  } catch (_) { /* sandbox contexts may throw */ }
  // Hostname autodetect — `<env>.my.symbols.app` → matching channel.
  const matched = _hostToChannel(window.location.hostname)
  if (matched) return channelApiUrl(matched)
  // Channel registry's own resolution — falls through to NODE_ENV →
  // SYMBOLS_API_CHANNEL → defaultChannel from the channels package.
  return channelApiUrl()
}
